/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { useManageIPMSContext } from "./ManageIPMSContex";
import moment from "moment";
// import {
//   OrgGoal,
//   otherResponse,
//   QResponseData,
//   QuaterDaum,
//   SoftGoal,
//   UpdateGoalsAssessmentsPayload,
// } from "../model/ipmsmodel";
import { AssessmentScale } from "./newipmsmodel";
import { getCookieData } from "../../helper/api";
import { GoalsAssessmentFlagStatusEnumTextCap } from "./constant";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { MicrophoneIcon, StopIcon } from "@heroicons/react/24/solid";

export interface Column<T> {
  header: string;
  key: keyof T;
  required: boolean;
  inputType: string;
  userRole?: number[];
  formTypes?: string[];
  width?: string;
  enableSpeech?: boolean;
  enableHoverPopup?: boolean;
}

interface CustomTableProps<T> {
  columns: Column<T>[];
  data: T[];
  tableKeyName: string;
  children?: React.ReactNode;
  quarterNameData: string;
  roles?: number | null;
  formTypeRule: string;
  assessmentStatus: string;
  assessment_scale?: AssessmentScale[];
}

interface SpeechTextAreaProps {
  value: string;
  disabled: boolean;
  readOnly: boolean;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  rows?: number;
}

const SpeechTextArea: React.FC<SpeechTextAreaProps> = ({
  value,
  disabled,
  readOnly,
  onChange,
  placeholder,
  className,
  rows = 4,
}) => {
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  const baseTextRef = React.useRef(value || "");
  const [isListeningLocal, setIsListeningLocal] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when value changes
  React.useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
    }
  }, [value]);

  // Sync local state: if global listening stops, we stop too.
  React.useEffect(() => {
    if (!listening) {
      setIsListeningLocal(false);
    }
  }, [listening]);

  // push transcript live into textarea
  React.useEffect(() => {
    if (listening && isListeningLocal) {
      if (transcript) {
        const baseText = baseTextRef.current;
        const separator = baseText && baseText.trim().length > 0 ? " " : "";
        const newValue = baseText + separator + transcript;
        if (newValue !== value) {
          onChange(newValue);
        }
      }
    } else if (!listening) {
        // Update base text ref when not listening, to capture manual edits
        if (value) {
             baseTextRef.current = value; 
        }
    }
  }, [transcript, listening, isListeningLocal, onChange, value]);

  return (
    <div className="relative h-full">
      <textarea
        ref={textareaRef}
        disabled={disabled}
        rows={rows}
        readOnly={readOnly}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={className}
        placeholder={placeholder}
      />
      {browserSupportsSpeechRecognition && !disabled && !readOnly && (
        <button
          type="button"
          onClick={() => {
            if (listening) {
              SpeechRecognition.stopListening();
            } else {
              baseTextRef.current = value || "";
              resetTranscript();
              setIsListeningLocal(true);
              SpeechRecognition.startListening({
                continuous: true,
                language: "en-IN",
              });
            }
          }}
          title="Dictate"
          className={`absolute top-2 right-5 p-1 rounded-full transition-all shadow-sm hover:shadow-md active:scale-95 cursor-pointer ${
            listening && isListeningLocal ? "bg-red-500 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {listening && isListeningLocal ? (
            <StopIcon className="w-4 h-4" />
          ) : (
            <MicrophoneIcon className="w-4 h-4" />
          )}
        </button>
      )}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface CommentHoverPopupProps {
  title: string;
  content: any;
  children: React.ReactNode;
}

const CommentHoverPopup: React.FC<CommentHoverPopupProps> = ({ title, content, children }) => {
  const [show, setShow] = React.useState(false);
  const [coords, setCoords] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const containerRef = React.useRef<HTMLDivElement>(null);

  const rawText = typeof content === "string" ? content : content ? String(content) : "";
  const formattedContent = rawText.split("\\r\\n").join("\n").trim();

  const handleMouseEnter = () => {
    if (!formattedContent) return;
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const popupWidth = 360;
      let leftPos = rect.left;
      if (leftPos + popupWidth > window.innerWidth - 20) {
        leftPos = window.innerWidth - popupWidth - 20;
      }
      setCoords({
        top: Math.max(rect.top - 8, 20),
        left: Math.max(leftPos, 10),
      });
    }
    setShow(true);
  };

  const handleMouseLeave = () => {
    setShow(false);
  };

  return (
    <div
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative w-full h-full"
    >
      {children}
      {show && formattedContent.length > 0 && (
        <div
          style={{
            position: "fixed",
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            transform: "translateY(-100%)",
          }}
          className="z-[99999] w-[26rem] max-w-xl p-4 bg-gray-900 text-white border border-gray-700 shadow-2xl rounded-xl transition-opacity duration-200 pointer-events-auto"
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
        >
          <div className="flex items-center justify-between border-b border-gray-700 pb-2 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-blue-400">
              {title}
            </span>
            <span className="text-[10px] bg-blue-900/80 text-blue-200 px-2 py-0.5 rounded-full font-medium">
              Scrollable View
            </span>
          </div>
          <div
            onWheel={(e) => e.stopPropagation()}
            className="text-xs leading-relaxed text-gray-100 whitespace-pre-wrap max-h-72 overflow-y-auto break-words pr-2"
          >
            {formattedContent}
          </div>
        </div>
      )}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IPMSCustomTable: React.FC<CustomTableProps<any>> = ({
  columns,
  data,
  children,
  tableKeyName,
  quarterNameData,
  roles,
  assessmentStatus,
  formTypeRule,
  assessment_scale,
}) => {
  const role = getCookieData("roleId") ? Number(getCookieData("roleId")) : roles;
  const { GetIpmsEmployeeGoalAssessmentData, dispatch } = useManageIPMSContext();

  const handleInputChange = (
    tableKey: string,
    index: number,
    columnKey: string,
    value: string,
  ) => {
    if (!GetIpmsEmployeeGoalAssessmentData) return;

    dispatch({
      type: "UPDATE_GOAL_ASSESSMENT",
      payload: {
        tableKey,
        index,
        columnKey,
        value,
        quarterName: quarterNameData,
      },
    });
  };

  return (
    <>
      <div className='relative overflow-x-auto sm:rounded-b-lg rounded-t-none mb-10 '>
        {children}
        <table className='w-full text-sm text-left rtl:text-right text-gray-500'>
          <thead className='text-md sticky z-40 top-0 text-center text-gray-700 uppercase bg-green-200 t'>
            <tr>
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  className='px-2 py-3 text-center text-sm border border-gray-300'
                  style={{ width: column.width ? (column.width as any) : 'auto' }}
                >
                  {column.header}
                  {column.required && <span className='text-red-500'>*</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='bg-white dark:bg-gray-800 text-left'>
            {data && data.map((item: any, index: number) => (
              <tr
                key={index}
                className='bg-white dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600'
              >
                {columns.map((column) => (
                  <td
                    key={String(column.key)}
                    className='px-2 py-3 border border-gray-200 dark:border-gray-600 text-left text-sm'
                  >
                    {
                      (column.key === "goaltimeline" || column.key === "target_date") && (
                        <p className='text-white'>{moment(item[column.key]).isValid() ? moment(item[column.key]).format("MMM YYYY") : ""}</p>
                      )
                    }
                    {column.inputType === "rating" && (
                      <div>
                        <div className='relative mb-6'>
                          <input
                            id='labels-range-input'
                            type='range'
                            value={item[column.key] ?? 0}
                            required={column.required}
                            disabled={[GoalsAssessmentFlagStatusEnumTextCap.APPROVED, GoalsAssessmentFlagStatusEnumTextCap.COMPLETED].includes(
                              assessmentStatus,
                            ) || !column.formTypes?.includes(formTypeRule)
                            }
                            readOnly={
                              [GoalsAssessmentFlagStatusEnumTextCap.APPROVED, GoalsAssessmentFlagStatusEnumTextCap.COMPLETED].includes(
                                assessmentStatus,
                              ) || column.formTypes?.includes(formTypeRule)
                            }
                            min='0'
                            max='5'
                            step='0.1'
                            onChange={(e) =>
                              handleInputChange(
                                tableKeyName,
                                index,
                                column.key as string,
                                e.target.value,
                              )
                            }
                            className='w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-200'
                          />
                          Rating: {item[column.key] ?? 0}
                        </div>
                      </div>
                    )}
                    {column.inputType === "selectbox" && column.key !== "adheringtogoal" && column.key !== "is_adhered" ? (
                      <select
                        value={item[column.key]?.toString() ?? ""}
                        required={column.required}
                        disabled={[GoalsAssessmentFlagStatusEnumTextCap.APPROVED, GoalsAssessmentFlagStatusEnumTextCap.COMPLETED].includes(
                          assessmentStatus,
                        ) || !column.formTypes?.includes(formTypeRule)
                        }
                        className={`block px-1.5 py-1.5 w-full z-5 text-sm bg-white border dark:border-gray-600 dark:bg-gray-700 dark:text-white text-black rounded-lg ${
                          !([
                            GoalsAssessmentFlagStatusEnumTextCap.APPROVED,
                            GoalsAssessmentFlagStatusEnumTextCap.COMPLETED,
                          ].includes(assessmentStatus) ||
                            !column.formTypes?.includes(formTypeRule))
                            ? item[column.key] === null || item[column.key] === undefined || item[column.key] === ""
                              ? "border-red-500 border-2"
                              : "border-green-500 border-2"
                            : ""
                        }`}
                        onChange={(e) =>
                          handleInputChange(
                            tableKeyName,
                            index,
                            column.key as string,
                            e.target.value,
                          )
                        }
                      >
                        <option value={""}>Select Rating</option>
                        {assessment_scale && assessment_scale.length > 0 ? (
                          assessment_scale.map((item) => (
                            <option key={item.rating} value={item.rating?.toString()}>
                              {item.rating} - {item.description}
                            </option>
                          ))
                        ) : (
                          <>
                            <option value={"1"}>1 - Below Expectations</option>
                            <option value={"2"}>2 - Needs Improvement</option>
                            <option value={"3"}>3 - Meets Expectations</option>
                            <option value={"4"}>4 - Exceeds Expectations</option>
                            <option value={"5"}>5 - Outstanding</option>
                          </>
                        )}
                      </select>
                    ) : (
                      (column.key === "adheringtogoal" || column.key === "is_adhered") && (
                        <select
                          value={item[column.key] === true ? "Y" : item[column.key] === false ? "N" : item[column.key]}
                          required={column.required}
                          disabled={[GoalsAssessmentFlagStatusEnumTextCap.APPROVED, GoalsAssessmentFlagStatusEnumTextCap.COMPLETED].includes(
                            assessmentStatus,
                          ) || !column.formTypes?.includes(formTypeRule)
                          }
                          className='block px-3 py-2 w-full z-5 text-sm bg-white border dark:border-gray-600 dark:bg-gray-700 dark:text-white text-black rounded-lg '
                          onChange={(e) =>
                            handleInputChange(
                              tableKeyName,
                              index,
                              column.key as string,
                              e.target.value,
                            )
                          }
                        >
                          <option value={""}>Select</option>
                          <option value={"Y"}>Yes</option>
                          <option value={"N"}>No</option>
                        </select>
                      )
                    )}
                    {column.key !== "hr_Inputs" && column.inputType === "textarea" && (() => {
                      const inputNode = column.enableSpeech ? (
                        <SpeechTextArea
                          value={item[column.key] || ""}
                          rows={4}
                          readOnly={
                            [GoalsAssessmentFlagStatusEnumTextCap.APPROVED, GoalsAssessmentFlagStatusEnumTextCap.COMPLETED].includes(
                              assessmentStatus,
                            ) || !column.formTypes?.includes(formTypeRule)
                          }
                          disabled={
                            [GoalsAssessmentFlagStatusEnumTextCap.APPROVED, GoalsAssessmentFlagStatusEnumTextCap.COMPLETED].includes(
                              assessmentStatus,
                            ) || !column.formTypes?.includes(formTypeRule)
                          }
                          onChange={(value) =>
                            handleInputChange(
                              tableKeyName,
                              index,
                              column.key as string,
                              value,
                            )
                          }
                          className={`block p-2 pr-10 w-full h-full text-left text-sm  text-gray-900 bg-white dark:bg-gray-700 dark:text-white  border-0`}
                        />
                      ) : (
                        <textarea
                          value={item[column.key] || ""}
                          rows={4}
                          readOnly={
                            [GoalsAssessmentFlagStatusEnumTextCap.APPROVED, GoalsAssessmentFlagStatusEnumTextCap.COMPLETED].includes(
                              assessmentStatus,
                            ) || !column.formTypes?.includes(formTypeRule)
                          }
                          id={`cvv-input-${index}`}
                          aria-describedby='helper-text-explanation'
                          onChange={(e) =>
                            handleInputChange(
                              tableKeyName,
                              index,
                              column.key as string,
                              e.target.value,
                            )
                          }
                          required={column.required}
                          className={`block p-2 w-full h-full text-left text-sm  text-gray-900 bg-white dark:bg-gray-700 dark:text-white  border-0`}
                        />
                      );
                      return column.enableHoverPopup ? (
                        <CommentHoverPopup title={column.header} content={item[column.key] || ""}>
                          {inputNode}
                        </CommentHoverPopup>
                      ) : (
                        inputNode
                      );
                    })()}

                    {/* {assessmentStatus} */}
                    {column.key === "hr_Inputs" && column.inputType === "textarea" && (() => {
                      const hrInputNode = column.enableSpeech ? (
                        <SpeechTextArea
                          value={
                            column.userRole?.includes(role as number)
                              ? item[column.key]
                              : ""
                          }
                          rows={4}
                          readOnly={
                            ![
                              GoalsAssessmentFlagStatusEnumTextCap.APPROVED,
                            ].includes(assessmentStatus)
                          }
                          disabled={
                            ![
                              GoalsAssessmentFlagStatusEnumTextCap.APPROVED,
                            ].includes(assessmentStatus)
                          }
                          onChange={(value) =>
                            handleInputChange(
                              tableKeyName,
                              index,
                              column.key as string,
                              value,
                            )
                          }
                          className={`block p-2 pr-10 w-full h-full text-left text-sm  text-gray-900 bg-white dark:bg-gray-700 dark:text-white  border-0`}
                        />
                      ) : (
                        <textarea
                          value={
                            column.userRole?.includes(role as number)
                              ? item[column.key]
                              : ""
                          }
                          rows={4}
                          readOnly={
                            ![
                              GoalsAssessmentFlagStatusEnumTextCap.APPROVED,
                            ].includes(assessmentStatus)
                          }
                          id={`cvv-input-${index}`}
                          aria-describedby='helper-text-explanation'
                          onChange={(e) =>
                            handleInputChange(
                              tableKeyName,
                              index,
                              column.key as string,
                              e.target.value,
                            )
                          }
                          required={column.required}
                          className={`block p-2 w-full h-full text-left text-sm  text-gray-900 bg-white dark:bg-gray-700 dark:text-white  border-0`}
                        />
                      );
                      return column.enableHoverPopup ? (
                        <CommentHoverPopup title={column.header} content={item[column.key] || ""}>
                          {hrInputNode}
                        </CommentHoverPopup>
                      ) : (
                        hrInputNode
                      );
                    })()}
                    {column.inputType === "input" && (
                      <input
                        id={`${quarterNameData}-${tableKeyName}-${String(column.key)}-${index}`}
                        type="text"
                        value={item[column.key] || ""}
                        readOnly={
                          [GoalsAssessmentFlagStatusEnumTextCap.APPROVED, GoalsAssessmentFlagStatusEnumTextCap.COMPLETED].includes(
                            assessmentStatus,
                          ) || !column.formTypes?.includes(formTypeRule)
                        }
                        onChange={(e) =>
                          handleInputChange(
                            tableKeyName,
                            index,
                            column.key as string,
                            e.target.value,
                          )
                        }
                        required={column.required}
                        autoComplete="off"
                        className='block scroll-mt-40 p-2 w-full text-sm text-gray-900 bg-white dark:bg-gray-700 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg'
                      />
                    )}
                    {column.inputType === "text" && (() => {
                      const textNode = (
                        <textarea
                          value={
                            column.key === "goaltimeline" || column.key === "target_date"
                              ? moment(item[column.key]).isValid()
                                ? moment(item[column.key]).format("DD MMM YYYY")
                                : ""
                              : typeof item[column.key] === "string"
                                ? item[column.key].split("\\r\\n").join("\n")
                                : item[column.key]
                          }
                          rows={4}
                          readOnly
                          id={`cvv-input-${index}`}
                          aria-describedby='helper-text-explanation'
                          className={`block p-2 w-full h-full text-sm text-left text-gray-900 bg-white dark:bg-gray-700 dark:text-white  border-0`}
                        />
                      );
                      return column.enableHoverPopup ? (
                        <CommentHoverPopup
                          title={column.header}
                          content={
                            column.key === "goaltimeline" || column.key === "target_date"
                              ? moment(item[column.key]).isValid()
                                ? moment(item[column.key]).format("DD MMM YYYY")
                                : ""
                              : item[column.key] || ""
                          }
                        >
                          {textNode}
                        </CommentHoverPopup>
                      ) : (
                        textNode
                      );
                    })()}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {(!data || data.length === 0) && <p className='p-3 text-sm w-full text-center'>No Record Found</p>}
      </div>
    </>
  );
};

export default IPMSCustomTable;
